// Declarative library/group spec (D15). Both the provisioning reconciler and the client read
// this static declaration — a new library is never just a drive id, it also needs to know which
// groups get which role on it. The reconciler has no per-library and no per-group code; it loops
// this spec.
//
// `key` is code-owned and must never be renamed once provisioned. `title` may change freely, but
// SharePoint derives a library's URL from its title *at creation* and does not follow later title
// changes — so libraries are always addressed by stored ids, never by name.
// SharePoint's `SPRoleType` enum — the language-independent handle on a *built-in* permission level.
//
// Sites are provisioned with `Lcid 1053` (sv-SE) and SharePoint **localizes the names of built-in
// role definitions**, so a Swedish site has `Läsa`/`Bidra`, never `Read`/`Contribute`. Looking one up
// by its English name fails with a 500 `SPException` ("Det går inte att hitta behörighetsnivån") —
// not a 404, so it does not even look like an absence. Always resolve by `RoleTypeKind`.
//
// Only the two kinds this spec grants are listed; D7 deliberately excludes Administrator (5).
export const SP_ROLE_TYPE = {
    /** `Read` / `Läsa` */
    Read: 2,
    /** `Contribute` / `Bidra` */
    Contribute: 3,
};
// Group *names* are ours — we create these six groups, so they are not localized and are safe to
// address by name. Only the role definitions bound to them are (see `SP_ROLE_TYPE`).
export const SITE_GROUPS = {
    CESG_EDIT: { name: 'CESG Edit', roleType: SP_ROLE_TYPE.Contribute },
    CESG_COMMENT: { name: 'CESG Comment', roleType: SP_ROLE_TYPE.Read },
    CESG_VIEW: { name: 'CESG View', roleType: SP_ROLE_TYPE.Read },
    GUEST_EDIT: { name: 'Guest Edit', roleType: SP_ROLE_TYPE.Contribute },
    GUEST_COMMENT: { name: 'Guest Comment', roleType: SP_ROLE_TYPE.Read },
    GUEST_VIEW: { name: 'Guest View', roleType: SP_ROLE_TYPE.Read },
};
/**
 * The app-admin principal's own site group (D6). Deliberately NOT a seventh `SITE_GROUPS` entry:
 * that constant is the enum of permission-derived tiers, and the membership resync iterates it to
 * compute desired state. A tier key here would make the resync compute an empty desired set for this
 * group and remove its only member on every sync.
 *
 * Its membership comes from Entra (D8) and is written once, at provisioning. Nothing in
 * `sharepoint-membership-service.ts` may ever read or resolve it.
 */
export const ADMIN_SITE_GROUP = {
    key: 'CESG_ADMIN',
    name: 'CESG Admin',
    roleType: SP_ROLE_TYPE.Contribute, // D7: Contribute, never Full Control
};
/**
 * Name/role lookup for every principal a library ACL can name. **Read-only lookup — never iterate
 * this to derive membership.** `SITE_GROUPS` is the membership enum; this is the ACL enum.
 */
export const LIBRARY_GRANT_GROUPS = {
    ...SITE_GROUPS,
    CESG_ADMIN: { name: ADMIN_SITE_GROUP.name, roleType: ADMIN_SITE_GROUP.roleType },
};
export const SITE_LIBRARIES = [
    { key: 'CESG', title: 'CESG', grants: ['CESG_EDIT', 'CESG_COMMENT', 'CESG_VIEW', 'CESG_ADMIN'] },
    {
        key: 'SHARED',
        title: 'Delad',
        grants: [
            'CESG_EDIT',
            'CESG_COMMENT',
            'CESG_VIEW',
            'GUEST_EDIT',
            'GUEST_COMMENT',
            'GUEST_VIEW',
            'CESG_ADMIN',
        ],
    },
];
const AUDIENCE_BY_GROUP = {
    CESG_EDIT: 'CESG',
    CESG_COMMENT: 'CESG',
    CESG_VIEW: 'CESG',
    GUEST_EDIT: 'GUEST',
    GUEST_COMMENT: 'GUEST',
    GUEST_VIEW: 'GUEST',
    CESG_ADMIN: 'CESG',
};
// Stable output order, independent of the order grants happen to be declared in.
const AUDIENCE_ORDER = ['CESG', 'GUEST'];
/**
 * Which role families can reach a library, derived from its `grants` — never hand-maintained. A
 * separate `sensitivity` field would be a claim about the ACL stored next to the ACL, and could
 * drift; `grants` is what actually becomes the ACL (same reasoning as D14).
 *
 * CESG -> ['CESG']; SHARED -> ['CESG', 'GUEST'] (deduped, stable order).
 */
export const audienceOf = (grants) => {
    const audiences = new Set(grants.map((grant) => AUDIENCE_BY_GROUP[grant]));
    return AUDIENCE_ORDER.filter((audience) => audiences.has(audience));
};
